import { useRef, useState } from "react";
import { addDays, format, isSameDay, startOfDay } from "date-fns";
import { CalendarIcon, ChevronDownIcon } from "lucide-react";

import { WFPButton } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export const withDate = (current: Date | null, day: Date | undefined): Date | null => {
  if (!day) {
    return null;
  }
  const next = new Date(day);
  if (current) {
    next.setHours(current.getHours(), current.getMinutes(), 0, 0);
  }
  return next;
};

export const withTime = (current: Date | null, time: string): Date | null => {
  const [hours, minutes] = time.split(":").map(Number);
  const next = current ? new Date(current) : new Date();
  next.setHours(hours || 0, minutes || 0, 0, 0);
  return next;
};

const pad = (value: number) => String(value).padStart(2, "0");

const toLabel = (minutesOfDay: number) =>
  `${pad(Math.floor(minutesOfDay / 60))}:${pad(minutesOfDay % 60)}`;

interface DateTimePickerProps {
  id: string;
  value: Date | null;
  onChange: (value: Date | null) => void;
  placeholder: string;
  timeAriaLabel: string;
  minHour?: number;
  maxHour?: number;
  minuteStep?: number;
  invalid?: boolean;
}

export const DateTimePicker = ({
  id,
  value,
  onChange,
  placeholder,
  timeAriaLabel,
  minHour = 0,
  maxHour = 23,
  minuteStep = 15,
  invalid,
}: DateTimePickerProps) => {
  const [dateOpen, setDateOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  // A day picked but not yet paired with a time is not a valid value, so it stays
  // here and `onChange` keeps emitting null until a slot is chosen.
  const [pendingDay, setPendingDay] = useState<Date | null>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const openTimeOnClose = useRef(false);

  const now = new Date();
  const day = value ?? pendingDay;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const allSlots = Array.from(
    { length: Math.floor(((maxHour - minHour) * 60) / minuteStep) + 1 },
    (_, index) => minHour * 60 + index * minuteStep
  );
  const lastSlotPassed = nowMinutes >= allSlots[allSlots.length - 1];
  const slots = isSameDay(day ?? now, now) ? allSlots.filter(slot => slot > nowMinutes) : allSlots;
  const firstSlot = slots[0] ?? minHour * 60;
  const selectedSlot = value ? value.getHours() * 60 + value.getMinutes() : null;

  const commitTime = (time: string) => {
    if (!time) {
      setPendingDay(day);
      onChange(null);
      return;
    }
    onChange(withTime(day, time));
    setPendingDay(null);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={dateOpen} onOpenChange={setDateOpen}>
        <PopoverTrigger asChild>
          <WFPButton
            type="button"
            variant="outline"
            id={id}
            aria-invalid={invalid}
            className={cn("justify-start font-normal", !day && "text-muted-foreground")}
          >
            <CalendarIcon className="size-4" />
            {day ? format(day, "dd/MM/yyyy") : placeholder}
          </WFPButton>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0"
          align="start"
          // Radix restores focus to the trigger from a timeout on unmount, and a focusin
          // outside an open popover dismisses it — so the slot list is opened here, after
          // focus has been moved to the time field.
          onCloseAutoFocus={event => {
            if (!openTimeOnClose.current) {
              return;
            }
            openTimeOnClose.current = false;
            event.preventDefault();
            timeInputRef.current?.focus();
            setTimeOpen(true);
          }}
        >
          <Calendar
            mode="single"
            selected={day ?? undefined}
            onSelect={selected => {
              setPendingDay(selected ?? null);
              if (value) {
                onChange(withDate(value, selected));
              }
              openTimeOnClose.current = !!selected && !value;
              setDateOpen(false);
            }}
            disabled={{ before: lastSlotPassed ? addDays(startOfDay(now), 1) : now }}
          />
        </PopoverContent>
      </Popover>

      <Popover open={timeOpen} onOpenChange={setTimeOpen}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              ref={timeInputRef}
              type="time"
              className="w-28 pr-8 [&::-webkit-calendar-picker-indicator]:hidden"
              aria-label={timeAriaLabel}
              aria-invalid={invalid}
              min={toLabel(firstSlot)}
              max={`${pad(maxHour)}:00`}
              value={value ? format(value, "HH:mm") : ""}
              onChange={event => commitTime(event.target.value)}
              onClick={() => setTimeOpen(true)}
            />
            <ChevronDownIcon className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-2 size-4 text-muted-foreground" />
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          className="max-h-60 w-(--radix-popover-trigger-width) overflow-y-auto p-1"
          onOpenAutoFocus={event => event.preventDefault()}
          // The list is portalled to document.body, so a surrounding Dialog's
          // react-remove-scroll lock cancels wheel/touch here. Stop the event one hop
          // early, at body, before its document listener sees it.
          onWheel={event => event.stopPropagation()}
          onTouchMove={event => event.stopPropagation()}
        >
          <div role="listbox" aria-label={timeAriaLabel} className="flex flex-col gap-0.5">
            {slots.map(slot => {
              const selected = slot === selectedSlot;
              return (
                <WFPButton
                  key={slot}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  variant={selected ? "default" : "ghost"}
                  size="sm"
                  ref={selected ? node => node?.scrollIntoView?.({ block: "center" }) : undefined}
                  className="justify-start font-normal tabular-nums"
                  onClick={() => {
                    commitTime(toLabel(slot));
                    setTimeOpen(false);
                  }}
                >
                  {toLabel(slot)}
                </WFPButton>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
